(() => {
  if (window.FramewerkVehicleCatalogue) return;
  const element = document.querySelector('[data-framewerk-vehicle-catalogue]');
  if (!element) return;
  let seed;
  try { seed = JSON.parse(element.textContent); } catch { return; }
  const shop = element.dataset.shop;
  if (!shop || seed.shop !== shop || !validData(seed.data)) return;

  const CACHE_KEY = `framewerk:vehicle-catalogue:v2:${shop}`;
  const SESSION_KEY = 'framewerk:vehicle-catalogue:v1';
  const REQUEST_KEY = '__framewerkVehicleCatalogueRequest';
  const FRESH_MS = 5 * 60 * 1000;
  const RETAIN_MS = 7 * 24 * 60 * 60 * 1000;
  const TIMEOUT_MS = 5000;
  let snapshot = seed.data;
  let storedAt = Number(seed.storedAt) || 0;
  let pending = null;
  let timer;
  let failures = 0;

  function validData(data) {
    return data && Array.isArray(data.vehicles) && data.vehicles.every((vehicle) =>
      vehicle && (typeof vehicle.id === 'number' || typeof vehicle.id === 'string')
      && String(vehicle.code || '').length > 0
      && typeof vehicle.make === 'string' && typeof vehicle.model === 'string');
  }

  for (const storageName of ['sessionStorage', 'localStorage']) {
    try {
      const cached = JSON.parse(window[storageName]?.getItem(CACHE_KEY) || 'null');
      if (cached?.shop === shop && validData(cached.data)
        && Number.isFinite(cached.storedAt) && cached.storedAt > storedAt
        && cached.storedAt <= Date.now() && Date.now() - cached.storedAt < RETAIN_MS) {
        snapshot = cached.data;
        storedAt = cached.storedAt;
      }
    } catch { continue; }
  }

  function publish() {
    window[REQUEST_KEY] = Promise.resolve(snapshot);
    try {
      window.sessionStorage?.setItem(SESSION_KEY, JSON.stringify({ storedAt: Date.now(), data: snapshot }));
    } catch { return; }
  }

  function persist() {
    const record = JSON.stringify({ shop, storedAt, data: snapshot });
    for (const storageName of ['sessionStorage', 'localStorage']) {
      try { window[storageName]?.setItem(CACHE_KEY, record); } catch { continue; }
    }
  }

  function schedule(delay) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      if (document.visibilityState === 'hidden') return;
      void refresh();
    }, delay);
  }

  function refresh() {
    if (pending) return pending;
    clearTimeout(timer);
    const controller = new AbortController();
    let timeout;
    const deadline = new Promise((_, reject) => {
      timeout = setTimeout(() => {
        controller.abort();
        reject(new Error('Vehicle catalogue refresh timed out'));
      }, TIMEOUT_MS);
    });
    const network = Promise.resolve().then(async () => {
      const response = await fetch('/apps/framewerk?resource=vehicles', {
        credentials: 'same-origin', cache: 'no-store',
        headers: { Accept: 'application/json' }, signal: controller.signal,
      });
      if (!response.ok) throw new Error('Vehicle catalogue is temporarily unavailable');
      const data = await response.json();
      if (!validData(data)) throw new Error('Invalid vehicle catalogue');
      return data;
    });
    pending = Promise.race([network, deadline])
      .then((data) => {
        snapshot = data;
        storedAt = Date.now();
        failures = 0;
        persist();
        publish();
        return snapshot;
      })
      .catch(() => {
        failures += 1;
        publish();
        return snapshot;
      })
      .finally(() => {
        clearTimeout(timeout);
        pending = null;
        schedule(failures ? Math.min(60000, 15000 * failures) : FRESH_MS);
      });
    return pending;
  }

  publish();
  window.FramewerkVehicleCatalogue = Object.freeze({
    getSnapshot: () => snapshot,
    refresh,
  });
  const resume = () => {
    if (document.visibilityState === 'hidden') return;
    publish();
    if (Date.now() - storedAt >= FRESH_MS || failures) void refresh();
  };
  document.addEventListener('visibilitychange', resume);
  window.addEventListener('online', () => { void refresh(); });
  window.addEventListener('pageshow', resume);
  if (Date.now() - storedAt >= FRESH_MS) void refresh();
  else schedule(FRESH_MS - (Date.now() - storedAt));
})();
